/**
 * AI Workspace Selection Sync Hook
 *
 * - Syncs the active Query Editor selection to each AI workspace via `activeSelection.json`
 * - Listens for AI selection requests (via `selection.request.json`) and queues them per tab
 *
 * Note: This is a Pro feature. Selection sync is disabled in Personal mode.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useTerminalStore } from '../store/terminalStore';
import { useAiSelectionStore } from '../store/aiSelectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isQueryTab } from '../../workspace/core/types/workspaceTypes';
import { useAppStore } from '../../../store/appStore';
import { useHasProAccess } from '../../../core/licensing';
import type { ActiveSelectionSyncData, EditorSelectionRange } from '../../../types/aiWorkspace';

function selectionKey(range: EditorSelectionRange | null): string | null {
  if (!range) return null;
  return `${range.startLineNumber}:${range.startColumn}-${range.endLineNumber}:${range.endColumn}`;
}

export function useAiWorkspaceSelectionSync(): void {
  // Check if AI workspace features are available (Pro access - reactive)
  const canUseAiWorkspace = useHasProAccess();

  const syncDebounceMs = useAppStore((state) => state.aiTerminalSettings.aiWorkspace.syncDebounceMs);
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedBySessionRef = useRef(
    new Map<string, { tabId: string | null; key: string | null; selectedText: string }>()
  );

  const sessions = useTerminalStore((s) => s.sessions);
  const aiSessions = sessions.filter((s) => s.type === 'ai');

  const activeTabId = useWorkspaceStore((s) => s.activeTabId);
  const tabs = useWorkspaceStore((s) => s.tabs);

  const activeTab = activeTabId ? tabs.find((t) => t.id === activeTabId) : null;
  const activeQueryTab = activeTab && isQueryTab(activeTab) ? activeTab : null;

  const activeSelection = useAiSelectionStore(
    useCallback(
      (s) => (activeQueryTab ? s.selectionsByTabId.get(activeQueryTab.id) : undefined),
      [activeQueryTab?.id]
    )
  );

  const syncActiveSelection = useCallback(() => {
    // Skip sync if not Pro - AI terminal still works but without workspace sync
    if (!canUseAiWorkspace) return;
    if (!window.electronAPI?.aiWorkspace) return;

    const data: ActiveSelectionSyncData | null = activeQueryTab
      ? {
          tabId: activeQueryTab.id,
          tabName: activeQueryTab.title,
          selection: activeSelection?.selection ?? null,
          selectedText: activeSelection?.selectedText ?? '',
        }
      : null;

    const nextKey = selectionKey(data?.selection ?? null);
    const nextText = data?.selectedText ?? '';

    for (const session of aiSessions) {
      const last = lastSyncedBySessionRef.current.get(session.id) ?? {
        tabId: null,
        key: null,
        selectedText: '',
      };

      const next = { tabId: data?.tabId ?? null, key: nextKey, selectedText: nextText };

      if (last.tabId === next.tabId && last.key === next.key && last.selectedText === next.selectedText) {
        continue;
      }

      lastSyncedBySessionRef.current.set(session.id, next);

      window.electronAPI.aiWorkspace.syncActiveSelection(session.id, data).catch((err) => {
        console.error(`[AiWorkspaceSelectionSync] Failed to sync selection to session ${session.id}:`, err);
      });
    }

    const currentSessionIds = new Set(aiSessions.map((session) => session.id));
    for (const sessionId of lastSyncedBySessionRef.current.keys()) {
      if (!currentSessionIds.has(sessionId)) {
        lastSyncedBySessionRef.current.delete(sessionId);
      }
    }
  }, [canUseAiWorkspace, activeQueryTab, activeSelection?.selection, activeSelection?.selectedText, aiSessions]);

  useEffect(() => {
    if (aiSessions.length === 0) return;

    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }

    if (!activeQueryTab) {
      syncActiveSelection();
      return;
    }

    syncTimerRef.current = setTimeout(() => {
      syncActiveSelection();
    }, syncDebounceMs);

    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, [aiSessions.length, activeQueryTab?.id, activeSelection?.updatedAt, syncActiveSelection, syncDebounceMs]);

  const setSelectionRequest = useAiSelectionStore((s) => s.setSelectionRequest);

  // Listen for selection requests from AI - only in Pro mode
  useEffect(() => {
    // Skip if not Pro - AI terminal still works but without workspace sync
    if (!canUseAiWorkspace) return;
    if (!window.electronAPI?.aiWorkspace) return;

    const unsubscribe = window.electronAPI.aiWorkspace.onSelectionRequested((event) => {
      setSelectionRequest({
        sessionId: event.sessionId,
        requestedAt: Date.now(),
        request: event.request,
      });
    });

    return () => {
      unsubscribe?.();
    };
  }, [canUseAiWorkspace, setSelectionRequest]);
}
